import { createContext, useState, useEffect } from 'react';
import * as authApi from '../services/auth-api';

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check authentication status on mount
  useEffect(() => {
    checkAuth();
  }, []);

  async function checkAuth() {
    try {
      setLoading(true);
      
      // Check if we have tokens in localStorage
      const idToken = localStorage.getItem('idToken');
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!idToken || !refreshToken) {
        setUser(null);
        setLoading(false);
        return;
      }
      
      // Try to verify the token
      const response = await authApi.checkAuth();
      if (response.valid && response.user) {
        setUser(response.user);
      } else {
        setUser(null);
      }
    } catch (err) {
      // 401 errors are expected when token is invalid/expired
      // The checkAuth function will have already tried to refresh
      if (err.status !== 401) {
        console.error('Auth check error:', err);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  async function login(email, password, newPassword, session) {
    try {
      setError(null);
      
      const credentials = { email, password };
      if (newPassword && session) {
        credentials.newPassword = newPassword;
        credentials.session = session;
      }
      
      const response = await authApi.login(credentials.email, credentials.password, credentials.newPassword, credentials.session);
      
      console.log('Login response:', response); // Debug logging
      
      // If password change is required, return the challenge info without setting user
      if (response.challengeName === 'NEW_PASSWORD_REQUIRED') {
        return response;
      }
      
      // Successful login - set user
      if (response.user) {
        console.log('Setting user:', response.user); // Debug logging
        setUser(response.user);
      } else {
        console.warn('No user in response'); // Debug logging
      }
      
      return response;
    } catch (err) {
      console.error('Login error in AuthContext:', err); // Debug logging
      setError(err.message);
      throw err;
    }
  }

  async function logout() {
    try {
      await authApi.logout();
    } catch (err) {
      console.error('Logout failed:', err);
    } finally {
      setUser(null);
    }
  }

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    checkAuth,
    isAuthenticated: !!user,
    isOrganizer: user?.role === 'Organizer' || user?.role === 'organizer',
    isCaptain: user?.role === 'Captain' || user?.role === 'captain',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
